import { useState } from 'react';

export type SortKey = 'episode-asc' | 'episode-desc' | 'time-desc' | 'time-asc';
export type StatusKey = 'all' | '已更新' | '待更新';

interface CategoryControlsProps {
  sortBy: SortKey;
  status: StatusKey;
  resultCount: number;
  onSortChange: (value: SortKey) => void;
  onStatusChange: (value: StatusKey) => void;
}

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: 'episode-asc', label: '集数 · 正序', icon: 'ri-sort-asc' },
  { key: 'episode-desc', label: '集数 · 倒序', icon: 'ri-sort-desc' },
  { key: 'time-desc', label: '更新时间 · 最新', icon: 'ri-calendar-line' },
  { key: 'time-asc', label: '更新时间 · 最早', icon: 'ri-history-line' },
];

const statusOptions: { key: StatusKey; label: string }[] = [
  { key: 'all', label: '全部状态' },
  { key: '已更新', label: '已更新' },
  { key: '待更新', label: '待更新' },
];

export default function CategoryControls({
  sortBy,
  status,
  resultCount,
  onSortChange,
  onStatusChange,
}: CategoryControlsProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const panel = (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-foreground-400">排序方式</p>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onSortChange(option.key)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sortBy === option.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-800/70 text-foreground-200 hover:bg-primary-500/20 border border-primary-900/40'
              }`}
            >
              <i className={option.icon}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-foreground-400">播放状态</p>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onStatusChange(option.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                status === option.key
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-800/70 text-foreground-200 hover:bg-secondary-500/20 border border-secondary-900/40'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop inline controls */}
      <div className="hidden md:block rounded-lg bg-background-900 border border-primary-900/40 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-sm font-semibold text-foreground-100">筛选与排序</h2>
          <span className="text-xs text-foreground-500">共 {resultCount} 集</span>
        </div>
        {panel}
      </div>

      {/* Mobile trigger + bottom sheet */}
      <div className="md:hidden flex items-center gap-3">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-background-900 border border-primary-900/40 px-4 py-2.5 text-sm text-foreground-100 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选排序
        </button>
        <span className="text-xs text-foreground-500">共 {resultCount} 集</span>
      </div>

      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
            onClick={() => setSheetOpen(false)}
          ></div>
          <div className="relative w-full rounded-t-2xl bg-background-900 border-t border-primary-500/40 p-5 pb-8 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-semibold text-foreground-50">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-200 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {panel}
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-6 w-full rounded-md bg-accent-500 py-3 text-sm font-semibold text-background-950 cursor-pointer"
            >
              查看结果（{resultCount} 集）
            </button>
          </div>
        </div>
      )}
    </>
  );
}